import React, { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import Header from "../../Global/Header/Header";
import EmailCollectionForm from "../EmailCollectionForm/EmailCollectionForm";
import EmailVerificationForm from "../EmailVerificationForm/EmailVerificationForm";
import ExternalKycProviderConsentForm from "../ExternalKycProviderConsentForm/ExternalKycProviderConsentForm";
import ExternalKycProviderIFrameForm from "../ExternalKycProviderIFrameForm/ExternalKycProviderIFrameForm";
import { kycStore } from "../KycContext/KycContext";
import {
  kycAvailableSteps,
  KycForm,
  KycStep,
} from "../KycContext/kycAvailableSteps";
import PhoneAndEmailCollectionForm from "../PhoneAndEmailCollectionForm/PhoneAndEmailCollectionForm";
import PhoneNumberCollectionForm from "../PhoneNumberCollectionForm/PhoneNumberCollectionForm";
import PhoneNumberVerificationForm from "../PhoneNumberVerificationForm/PhoneNumberVerificationForm";
import ProgressBarLine from "../ProgressBarLine/ProgressBarLine";
import ThankYou from "../ThankYou/ThankYou"; //this must come after import './KycHome.scss';
import "./KycHome.scss";

interface KycQueryParams {
  contactInfoClientSessionId: string;
  contactInfoSessionId: string;
  steps?: string;
}

export default function KycHome() {
  const {
    kycState,
    startNewKycClientSession,
    continueKycClientSession,
    appInsightsTrackEvent,
  } = React.useContext(kycStore);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const attemptToContinueKycSession = useCallback(
    (query: KycQueryParams) => {
      appInsightsTrackEvent({
        name: "attemptToContinueKycSession starting",
        properties: {
          query: query,
        },
      });

      let kycSteps: KycStep[] | undefined = undefined;

      if (query.steps) {
        const stepsString: string = query.steps;

        kycSteps = [];
        const splitted = stepsString.split(" ");
        for (let index = 0; index < splitted.length; index++) {
          const kycStep = kycAvailableSteps.find(
            (s) => s.kycStep.toUpperCase() === splitted[index].toUpperCase()
          );

          if (kycStep) {
            kycSteps[index] = kycStep.kycStep;
          }
        }

        if (kycSteps.length > 0) {
          appInsightsTrackEvent({
            name: "attemptToContinueKycSession found steps in url",
            properties: {
              kycSteps: kycSteps,
            },
          });

          kycSteps.push(KycStep.ThankYou);
        }
      }

      continueKycClientSession(
        query.contactInfoClientSessionId,
        query.contactInfoSessionId,
        kycSteps
      );

      navigate("/", { replace: true });
    },
    [appInsightsTrackEvent, continueKycClientSession, navigate]
  );

  useEffect(() => {
    const contactInfoClientSessionId = searchParams.get(
      "contactInfoClientSessionId"
    );
    const contactInfoSessionId = searchParams.get("contactInfoSessionId");
    const steps = searchParams.get("steps");

    if (contactInfoClientSessionId && contactInfoSessionId) {
      const query: KycQueryParams = {
        contactInfoClientSessionId,
        contactInfoSessionId,
        ...(steps && { steps }),
      };
      attemptToContinueKycSession(query);
    } else {
      startNewKycClientSession();
    }
  }, [searchParams]);

  const getCurrentForm = () => {
    switch (kycState.currentForm) {
      case KycForm.PhoneNumberCollection:
        return <PhoneNumberCollectionForm />;
      case KycForm.PhoneNumberVerification:
        return <PhoneNumberVerificationForm />;
      case KycForm.EmailCollection:
        return <EmailCollectionForm />;
      case KycForm.EmailVerification:
        return <EmailVerificationForm />;
      case KycForm.ExternalKycProviderConsent:
        return <ExternalKycProviderConsentForm />;
      case KycForm.ExternalKycProviderSelfie:
      case KycForm.ExternalKycProviderLiveness:
      case KycForm.ExternalKycProviderIdScan:
        return <ExternalKycProviderIFrameForm />;
      case KycForm.ThankYou:
        return <ThankYou />;
      case KycForm.PhoneAndEmailCollection:
        return <PhoneAndEmailCollectionForm />;
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Filbo Identity</title>
        <meta name="description" content="Identitatatea ta digitală" />
      </Helmet>
      <Header>Verificare cu selfie și carte de identitate</Header>

      <Container className="kyc-container">
        <ProgressBarLine
          className="kyc-prog-bar"
          percent={kycState.percent}
        ></ProgressBarLine>
        {getCurrentForm()}
      </Container>
    </React.Fragment>
  );
}
